import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ClientsListQueryInterface } from '@zaoblako/shared/modules/client/dtos/client/clients-list-query.dto';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';
import { map } from 'rxjs/operators';

@Component({
  selector: 'crm-client-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, SpinnerComponent],
  templateUrl: './client-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientMultiSelectComponent implements OnInit {
  private static readonly ENTITY_CACHE_KIND = 'client';

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  /** Float label for the inner multi-select (default «Клиент»). */
  @Input() public selectLabel = 'Клиент';

  @ViewChild('multiSelectComponent', { static: true }) public multiSelectComponent: MultiSelectComponent;

  @Input() public clients: ClientFindInterface[] = [];
  public queryParams: ClientsListQueryInterface = {
    limit: 40,
    offset: 0,
    select: ['id', 'name']
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;
  /** True while selected IDs from parent are being resolved into entities. */
  public selectionLoading = false;

  public _selectedClients: ClientFindInterface[] = [];
  @Input() public set selectedClients(selectedClients: ClientFindInterface[]) {
    if (this.customSearch) {
      this._selectedClients = selectedClients;
      return;
    }

    if (selectedClients?.every((selectedClient) => this.clients.some((client) => client.id === selectedClient.id))) {
      this._selectedClients = this.clients.filter((client) =>
        selectedClients?.some((selectedClient) => client.id === selectedClient.id)
      );
      return;
    }

    const ids = selectedClients
      ?.filter((selectedClient) => !this.clients.some((client) => client.id === selectedClient.id))
      .map((selectedClient) => selectedClient.id);
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }
    if (ids?.length) {
      this.entityCache
        .loadByIds(ClientMultiSelectComponent.ENTITY_CACHE_KIND, ids, (missing) =>
          this.clientService
            .findWithPagination({
              includeIds: missing as number[],
              limit: missing.length > 500 ? 500 : missing.length,
              offset: 0
            })
            .pipe(map((data) => data.items))
        )
        .subscribe({
          next: (items) => {
            this.clients = this.clients.concat(
              items.filter((item) => !this.clients.some((client) => client.id === item.id))
            );
            this.cdRef.markForCheck();
            setTimeout(() => {
              this._selectedClients = this.clients.filter((client) => ids.includes(client.id));
              this.cdRef.markForCheck();
            }, 30);
          }
        });
    }
  }

  @Output() public selectedClientsChange = new EventEmitter<ClientFindInterface[]>();
  @Output() public openedChange = new EventEmitter<boolean>();

  @Input() public scrollHeight: string = '300px';
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;

  @Input() public customSearch = false;

  @Input() public hasPacsExternalUsers = false;

  @Input() public isCreateTransitTask = false;

  @Input() public set setSelectedClientIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }

    if (
      ids?.length &&
      ((this._selectedClients?.length &&
        !this._selectedClients?.every((selectedClient) => ids.includes(selectedClient.id))) ||
        !this._selectedClients?.length)
    ) {
      this.selectionLoading = true;
      this.cdRef.markForCheck();
      let newIds = ids.filter((id) => !this.clients.some((contragent) => contragent.id === id));
      if (newIds?.length) {
        this.entityCache
          .loadByIds(ClientMultiSelectComponent.ENTITY_CACHE_KIND, newIds, (missing) =>
            this.clientService
              .findWithPagination({
                includeIds: missing as number[],
                limit: missing.length > 500 ? 500 : missing.length,
                offset: 0
              })
              .pipe(map((data) => data.items))
          )
          .subscribe({
            next: (items) => {
              this.clients = this.clients.concat(
                items.filter((item) => !this.clients.some((client) => client.id === item.id))
              );
              this.cdRef.markForCheck();
              setTimeout(() => {
                this.applySelectionFromRequestedIds(ids);
              }, 30);
            },
            error: () => {
              this.selectionLoading = false;
              this.onSelect([]);
              this.cdRef.markForCheck();
            }
          });
      } else {
        this.applySelectionFromRequestedIds(ids);
      }
    } else if (!ids?.length) {
      this.selectionLoading = false;
    }
  }

  public constructor(
    private readonly clientService: ClientService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService,
    private readonly entityCache: DropdownEntityCacheService
  ) {}

  public ngOnInit(): void {
    if (
      !this.customSearch &&
      this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
    ) {
      this.loading = true;
      this.cdRef.markForCheck();

      const fingerprint = this.entityCache.fingerprint({
        ...this.queryParams,
        hasPacsExternalUsers: this.hasPacsExternalUsers,
        isCreateTransitTask: this.isCreateTransitTask
      });

      this.entityCache
        .loadFirstPage(ClientMultiSelectComponent.ENTITY_CACHE_KIND, fingerprint, () =>
          this.clientService.findWithPagination({
            ...this.queryParams,
            ...this.clientListFilters()
          })
        )
        .subscribe({
          next: (data) => {
            this.clients = this.clients?.length
              ? this.clients.concat(data.items.filter((item) => !this.clients.some((client) => client.id === item.id)))
              : data.items.filter((item) => !this.clients.some((client) => client.id === item.id));
            this.count = data.count;
            this.loading = false;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.loading = false;
            this.cdRef.markForCheck();
          }
        });
    }
  }

  public onSelect(clients: ClientFindInterface[]) {
    this.entityCache.putEntities(ClientMultiSelectComponent.ENTITY_CACHE_KIND, clients ?? []);
    this.selectedClientsChange.emit(clients);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.multiSelectComponent?.multiSelect?.scroller.onScroll.subscribe(() => {
          let el: HTMLElement = this.multiSelectComponent?.multiSelect?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.clients.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.clientService
                .findWithPagination({ ...this.queryParams, ...this.clientListFilters() })
                .subscribe({
                  next: (data) => {
                    this.clients = this.clients.concat(
                      data.items.filter((item) => !this.clients.some((client) => client.id === item.id))
                    );
                    this.count = data.count;
                    this.paginationLoading = false;
                    this.cdRef.markForCheck();
                  },
                  error: () => {
                    this.paginationLoading = false;
                    this.cdRef.markForCheck();
                  }
                });
            }
          }
        });
      }, 100);
    }
  }

  private clientListFilters(): Pick<ClientsListQueryInterface, 'hasPacsExternalUsers' | 'isCreateTransitTask'> {
    return {
      hasPacsExternalUsers: this.hasPacsExternalUsers,
      ...(this.isCreateTransitTask ? { isCreateTransitTask: true } : {})
    };
  }

  public searchClient(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.clientService
        .findWithPagination({ ...this.queryParams, ...this.clientListFilters() })
        .subscribe({
          next: (data) => {
            this.clients = data.items.concat(
              this._selectedClients?.filter(
                (selectedClient) => !data.items.some((client) => client.id === selectedClient.id)
              )
            );
            this.count = data.count;
            this.loading = false;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.loading = false;
            this.cdRef.markForCheck();
          }
        });
    }
  }

  private applySelectionFromRequestedIds(requestedIds: number[]): void {
    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.clients, requestedIds);
    this._selectedClients = selected;
    this.selectionLoading = false;
    if (hasStaleKeys) {
      this.onSelect(selected);
    }
    this.cdRef.markForCheck();
  }
}
